import { CeilingType } from "building-types/ceilings/ceiling";
import {
  ColumnHorizontalAlignment,
  ColumnLateralAlignment,
  ColumnPlacementType,
} from "building-types/columns/columnTypesEnum";
import { ColumnHorizontalDatum, WallLateralDatumAnchorAll } from "building-types/datums/datumsTypes";
import type { Doc } from "building-types/doc";
import { BASE_PRESET_NAME } from "building-types/doc/docDefaultConstants";
import { docEntityIdFromString } from "building-types/doc/docEntityId";
import type { DocEntityType } from "building-types/doc/docEntityTypes";
import { ElectricalDeviceType } from "building-types/electrical/electricalDeviceTypesEnum";
import type { ElectricalLayoutType } from "building-types/electrical/electricalLayoutTypesEnum";
import type { DocEntityTypeToValueType } from "building-types/entities/docEntityTypeToValueType";
import type { Label } from "building-types/entities/labelTypes";
import type { ReportableUnitDesignation } from "building-types/entities/reportableUnitDesignationTypes";
import type { EntitySettings } from "building-types/entitySettings/entitySettingsTypes";
import {
  FloorAssemblyType,
  HorizontalFloorAssemblyType,
  type FloorAssemblyOverride,
  type HorizontalFloorAssemblyRoomDerived,
} from "building-types/floors/floorAssemblyTypes";
import { StructuralFloorType } from "building-types/floors/floorComponentsTypes";
import { defaultFloorSystemFramingValues } from "building-types/floors/floorSystemTypes";
import type {
  ContentTemplateHandle,
  ContentTemplateInstanceHandle,
  DocEntityHandle,
  FoundationSystemHandle,
  FloorSystemHandle,
  GroupHandle,
  RoofHandle,
  RoofSystemHandle,
  RoomHandle,
  ViewTemplateHandle,
} from "building-types/handles/docEntityHandleTypes";
import type { ComponentHandle } from "building-types/handles/docEntityHandleTypes";
import type { LabelableEntityHandle } from "building-types/handles/labelableEntityHandle";
import { LevelPlacementType } from "building-types/levels/levelTypes";
import { deriveInstanceOptionSetId } from "building-types/options/instanceOptionSetId";
import type { OptionChoices } from "building-types/options/optionChoices";
import type { DocOptionSet, InstanceOptionSet } from "building-types/options/optionSet";
import type { Preset } from "building-types/options/preset";
import { PresetType } from "building-types/options/preset";
import { DEFAULT_TRIM_PROFILE_DESCRIPTOR } from "building-types/profiles/defaultBoardTrimProfiles";
import { ProfileExtrusionDescriptorType } from "building-types/profiles/profileTypesEnum";
import { PropCategory } from "building-types/props/propCategoryTypes";
import { DEFAULT_PROP_ID } from "building-types/props/propTypes";
import {
  CURRENT_GABLE_ROOF_VERSION,
  type Roof,
  type RoofDormerPlacement,
  type RoofOpeningPlacement,
  type SkylightPlacement,
} from "building-types/roofs/roofSystemTypes";
import type { RoomLayoutEntityType } from "building-types/roomLayouts/roomLayoutTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import type { StaticRoom } from "building-types/rooms/staticRoomTypes";
import type { ElectricalSettings } from "building-types/settings/electricalSettings";
import { BuildingSide, defaultRoofEdgeOverrides } from "building-types/settings/roofTypes";
import type { SettingsFragment } from "building-types/settings/settingsFragment";
import type { SettingsSet } from "building-types/settings/settingsSet";
import { SettingsSetBehaviorType } from "building-types/settings/settingsSetBehaviorType";
import { BUILT_IN_SETTINGS_TYPE_PREFIX, SettingsType } from "building-types/settings/settingsType";
import { SiteShapeType } from "building-types/site/siteShape";
import type { DrawingViewport, Viewport } from "building-types/views/planSets";
import { type ContentTemplateInstance } from "building-types/views/planSets";
import { planSetTemplateConfigNew } from "building-types/views/planSetTemplateTools";
import { wallPlaneFilterNew } from "building-types/walls2/wallPlaneFilters";
import {
  wallPlaneAnchoredRuleNew,
  wallFaceRuleNew,
  type WallPlaneRuleEntity,
} from "building-types/walls2/wallPlaneRules";
import { wallPlaneTrimEntityNew } from "building-types/walls2/wallPlaneTrimTypes";
import { BuiltinWindowId } from "building-types/windows/windowId";
import type { Table } from "common/data/table";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import objectFilter from "common/utils/objectFilter";
import { batchMergeIn, mergeIn } from "common/utils/objectUtils";
import { maxSortIndex, nextSortIndexFromTable } from "common/utils/sortIndex";
import { snakeCaseToTitleCase } from "common/utils/strings";
import type { Mutable } from "common/utils/types";
import uid from "common/utils/uid";
import { defaultRailingSetting } from "doc/elements/railing/constants";
import { contentTemplateDefaultBaseName } from "doc/entity/contentTemplateDefaultBaseName";
import { mat3Translate } from "geom/mat3";
import { edgeIdsByPolyLine2 } from "geom/polyLine2";
import { polyLine2New } from "geom/polyLine2Types";
import {
  contentTemplateAllowsViewportOrDataTable,
  getContentTemplateSubType,
  isDrawingTemplate,
  isValidParentForDrawingTemplateInstance,
} from "../contentTemplateTools";
import {
  absoluteVerticalDatumEntityNew,
  foundationLateralDatumEntityNew,
  foundationVerticalDatumEntityNew,
  levelDatumEntityNew,
  wallHorizontalDatumEntityNew,
  wallPlaneHorizontalDatumEntityNew,
  wallVerticalDatumEntityNew,
} from "../datums/datumEntityNew";
import { ceilingTypeToDefaultProps } from "../defaults/ceilingDefaults";
import { defaultSiteValues } from "../defaults/siteDefaults";
import {
  docAddComponentMembers,
  docCopyStructureForMembers,
  docEnforceComponentStructure,
  docGetComponentRecursiveOptionSets,
} from "../docComponentMembers";
import { docRefEligibleEntityTypeToMutableChildTypes } from "../docEntityReadonly";
import { docGetSolidFloorSystemFloors } from "../docGetFloors";
import { docGetFoundationSystemFoundations } from "../docGetFoundationSystems";
import { docGetRoofDormers, docGetRoofRoofOpenings, docGetRoofSkylights, docGetRoofSystemRoofs } from "../docGetRoofs";
import { docGetSettingsSetByBehaviorType } from "../docGetSettingsSets";
import { docMergeLabelTableProps, docMergeViewTemplateOverridesProps } from "../docMerge";
import { elevationViewNew, planViewNew, sectionViewNew, view3DNew } from "../docViewTypes";
import { defaultRelativeVerticalDatumData } from "../elements/columns/columnDefaults";
import { defaultColumnPlacementV2 } from "../elements/columns/columnPlacementUtils";
import { docGetBlockRooms } from "../elements/docGetBlocks";
import { defaultRoomDerivedRefProps } from "../elements/floor/floorAssembly";
import { floorAssemblyDynamicFinishLayerOperatorNew } from "../elements/floor/floorFinish";
import { defaultFloorNameForHorizontalAssemblyType } from "../elements/floor/floorShapeModeTools";
import { defaultAssembledStairConfig } from "../elements/stairSystem/stairConstants";
import { docMergeElevationOptionSet } from "../elevation";
import type { LogEntry } from "../log";
import { newLog } from "../log";
import { newPresetInt } from "../options/presetTools";
import { parameterFormatName } from "../parameterCoreTools";
import { defaultExportPermissions } from "../plans/exportPermissions";
import { mergedPropLibraryNew, propCatalogItemNew } from "../propTools";
import { getRoofHandleAndEdgeIdFromHandle, isRoofRelativeHandle } from "../roof/roofTools";
import { getRoomHandleAndEdgeIdFromHandle, isRoomLayoutRelatedHandle } from "../rooms/layout/roomLayoutTools";
import { ruleBasedSelectorNew } from "../ruleBasedSelectorNew";
import { entityIsGlobalSettingsSet, isBuiltInSettings } from "../settingsTools";
import { annotationTypeLabels } from "../utils/annotation";
import { isCaseWorkEntity } from "../utils/caseworkDescriptorUtils";
import { defaultColumnStructuralColumnId, defaultWoodPostMaterial } from "../utils/columnDescriptorUtils";
import { ElectricalLayoutTypeName } from "../utils/electricalLayoutUtils";
import {
  docGetLabelableEntitiesWithLabel,
  docReportableUnitDesignationBreaksOptionSetCap,
  labelNamePredicate,
  makeBuiltInLabelTable,
} from "../utils/labelTools";
import { boardProfileEntityNew, customProfileEntityNew } from "../utils/profileTools";
import { defaultRoofSideOverrides } from "../utils/roofTools";
import {
  relaxationObstacleEffectNew,
  scalingEffectNew,
  stylingEffectNew,
  visibilityEffectNew,
} from "../views/effectTools";
import { docContentTemplateInstanceCanBeInsertedWithoutSelfReference } from "./contentTemplateDependencies";
import { docEntityHandleHash } from "./core/handleHash";
import { docAddEntitiesToGroup } from "./docAddEntitiesToGroup";
import { docAddEntityToRoomLayoutIfRoomLayoutHandle } from "./docAddEntityToRoomLayoutIfRoomLayoutHandle";
import type { DocEntity } from "./docEntity";
import { assertIsDocEntityBase } from "./docEntityBaseTools";
import { docEntityTypeRequiresParent } from "./docEntityConstraintTools";
import { docEntityGetChildrenFromTable } from "./docEntityGetChildrenFromTable";
import { docEntityIsGroupable } from "./docEntityGroupable";
import { docEntityHandleToEntityRef } from "./docEntityHandleToEntityRef";
import { isEntitySettingsEligibleHandle } from "./docEntityHandleTools";
import { docEntityOrParentIsLocked } from "./docEntityLockable";
import { docEntityNameWithCount } from "./docEntityNameWithCount";
import { docEntityNameWithCountFromTable } from "./docEntityNameWithCountFromTable";
import { docEntityTypeToDocTableName } from "./docEntityTypeToDocTableName";
import type { DocEntityTypeToParentHandleType } from "./docEntityTypeToParentHandleType";
import docGetEntity from "./docGetEntity";
import { docEntityGetComponentOrAnnotationHost, isComponentMemberEntityHandle } from "./docGetEntityComponent";
import { docGetEntityHandleById } from "./docGetEntityHandleById";
import { docGetEntityId } from "./docGetEntityId";
import { docGetEntitySettingsForEntity } from "./docGetEntitySettings";
import { docGetTableEntity } from "./docGetTableEntity";
import type { TableBasedAddDocEntityType } from "./tableBasedDocEntityHandle";

export function isTableBasedDocEntityType(entityType: DocEntityType): entityType is TableBasedAddDocEntityType {
  return Object.keys(docEntityTypeToDocTableName).includes(entityType);
}

type DocEntityDefaultValueSource<T extends TableBasedAddDocEntityType> = (
  props: Partial<DocEntityTypeToValueType[T]>,
  doc: Doc,
  parent?: DocEntityTypeToParentHandleType[T],
) => Partial<DocEntityTypeToValueType[T]> | null;

type DocEntityDefaultSortIndexSource<T extends TableBasedAddDocEntityType> = (
  props: Partial<DocEntityTypeToValueType[T]>,
  doc: Doc,
  parent?: DocEntityTypeToParentHandleType[T],
) => number | undefined;

const docEntityTypeToDefaultSortIndex: {
  [P in TableBasedAddDocEntityType]?: DocEntityDefaultSortIndexSource<P>;
} = {
  FOUNDATION: (_props, doc, parent): number | undefined => {
    if (parent?.type !== "FOUNDATION_SYSTEM") {
      return undefined;
    }
    return maxSortIndex(docGetFoundationSystemFoundations(doc, parent as FoundationSystemHandle)) + 1;
  },
  FLOOR: (_props, doc, parent): number | undefined => {
    if (parent?.type !== "FLOOR_SYSTEM") {
      return undefined;
    }
    return maxSortIndex(docGetSolidFloorSystemFloors(doc.floors, parent as FloorSystemHandle)) + 1;
  },
  ROOF: (_props, doc, parent): number | undefined => {
    if (parent?.type !== "ROOF_SYSTEM") {
      return undefined;
    }
    return maxSortIndex(docGetRoofSystemRoofs(doc, parent as RoofSystemHandle)) + 1;
  },
  DORMER: (_props, doc, parent): number | undefined => {
    if (parent?.type !== "ROOF") {
      return undefined;
    }
    return maxSortIndex(docGetRoofDormers(doc, parent as RoofHandle)) + 1;
  },
  SKYLIGHT: (_props, doc, parent): number | undefined => {
    if (parent?.type !== "ROOF") {
      return undefined;
    }
    return maxSortIndex(docGetRoofSkylights(doc, parent as RoofHandle)) + 1;
  },
  ROOF_OPENING: (props, doc, parent): number | undefined => {
    if (parent?.type !== "ROOF") {
      return undefined;
    }
    return maxSortIndex(docGetRoofRoofOpenings(doc, parent as RoofHandle, props.side)) + 1;
  },
};

/**
 * Add to this to customize entity creation defaults
 */

const docEntityTypeToDefaultValues: {
  [P in TableBasedAddDocEntityType]?: DocEntityDefaultValueSource<P>;
} = {
  STAIR_SYSTEM: (): Partial<DocEntityTypeToValueType["STAIR_SYSTEM"]> => {
    return {
      contextAware: true,
      labels: {},
    } as Partial<DocEntityTypeToValueType["STAIR_SYSTEM"]>;
  },
  RAILING: (): Partial<DocEntityTypeToValueType["RAILING"]> => {
    return {
      labels: {},
      useNewRailingTerminationLogic: true,
    } as Partial<DocEntityTypeToValueType["RAILING"]>;
  },
  STAIR_SETTING: (): Partial<DocEntityTypeToValueType["STAIR_SETTING"]> => {
    return {
      catalogItemType: "STAIR_SETTING",
      config: defaultAssembledStairConfig,
    } as Partial<DocEntityTypeToValueType["STAIR_SETTING"]>;
  },
  FREEFORM_WALL: (): Partial<DocEntityTypeToValueType["FREEFORM_WALL"]> => {
    return {
      labels: {},
      path: null,
      edgeIds: [],
    } as Partial<DocEntityTypeToValueType["FREEFORM_WALL"]>;
  },
  FLOOR_SYSTEM: (): Partial<DocEntityTypeToValueType["FLOOR_SYSTEM"]> => {
    return {
      ...defaultFloorSystemFramingValues,
      labels: {},
    } as Partial<DocEntityTypeToValueType["FLOOR_SYSTEM"]>;
  },
  FOUNDATION: (props, doc, parent): Partial<DocEntityTypeToValueType["FOUNDATION"]> => {
    const foundations =
      parent?.type === "FOUNDATION_SYSTEM"
        ? docGetFoundationSystemFoundations(doc, parent as FoundationSystemHandle)
        : [];

    return {
      name: props.name ?? docEntityNameWithCount("Foundation", foundations),
      labels: props.labels ?? {},
    };
  },
  FLOOR: (props, doc, parent): Partial<DocEntityTypeToValueType["FLOOR"]> => {
    // FLOOR is also remap-eligible (docRemapEntityOneToTwoRooms clones an existing floor onto a
    // newly split room via this same generic path, parented to the ROOM rather than a FLOOR_SYSTEM).
    // The assembly-type defaulting below is ADD_FLOOR/ADD_FLOOR_SYSTEM-specific business logic, not
    // something a structural clone wants applied on top of its already-complete props.
    if (parent?.type !== "FLOOR_SYSTEM") {
      return { labels: props.labels ?? {} } as Partial<FloorAssemblyOverride>;
    }

    const floors = docGetSolidFloorSystemFloors(doc.floors, parent as FloorSystemHandle);
    const assemblyType = props.assemblyType ?? FloorAssemblyType.SOLID;
    const horizontalAssemblyType = props.horizontalAssemblyType ?? HorizontalFloorAssemblyType.FREE_FORM;
    const floorNameType =
      horizontalAssemblyType === HorizontalFloorAssemblyType.ROOM_DERIVED
        ? HorizontalFloorAssemblyType.ROOM_DERIVED
        : HorizontalFloorAssemblyType.FREE_FORM;
    const defaultName =
      assemblyType === FloorAssemblyType.VOID ? "Void Floor" : defaultFloorNameForHorizontalAssemblyType(floorNameType);
    const roomDerivedRefProps =
      horizontalAssemblyType === HorizontalFloorAssemblyType.ROOM_DERIVED
        ? defaultRoomDerivedRefProps(props as Partial<HorizontalFloorAssemblyRoomDerived>)
        : {};

    return {
      labels: props.labels ?? {},
      assemblyType,
      horizontalAssemblyType,
      ...(assemblyType === FloorAssemblyType.SOLID
        ? { structuralType: props["structuralType"] ?? StructuralFloorType.FRAMING }
        : {}),
      ...roomDerivedRefProps,
      name: props.name ?? docEntityNameWithCount(defaultName, floors),
      disableFoundationAssembly: true,
    } as Partial<FloorAssemblyOverride>;
  },
  CEILING: (props, doc): Partial<DocEntityTypeToValueType["CEILING"]> => {
    const defaultCeiling = ceilingTypeToDefaultProps[props.type ?? CeilingType.FLAT];
    return {
      name: props.name ?? docEntityNameWithCount("Ceiling", Object.values(doc.ceilings)),
      ...defaultCeiling,
    } as Partial<DocEntityTypeToValueType["CEILING"]>;
  },
  ROOF: (props, doc, parent): Partial<DocEntityTypeToValueType["ROOF"]> => {
    const roofs = parent?.type === "ROOF_SYSTEM" ? docGetRoofSystemRoofs(doc, parent as RoofSystemHandle) : [];

    return {
      blocks: [],
      name: props.name ?? docEntityNameWithCount("Roof", roofs),
      labels: props.labels ?? {},
      roofSideOverrides: defaultRoofSideOverrides,
      roofEdgeOverrides: defaultRoofEdgeOverrides,
      roofVersion: CURRENT_GABLE_ROOF_VERSION,
    } as Partial<Roof>;
  },
  DORMER: (props, doc, parent): Partial<DocEntityTypeToValueType["DORMER"]> => {
    const dormers = parent?.type === "ROOF" ? docGetRoofDormers(doc, parent as RoofHandle) : [];

    return {
      dormerSettings: { side: BuildingSide.FRONT },
      name: props.name ?? docEntityNameWithCount("Dormer", dormers),
      useNewPlacementMethod: true,
    } as Partial<RoofDormerPlacement>;
  },
  SKYLIGHT: (props, doc, parent): Partial<DocEntityTypeToValueType["SKYLIGHT"]> => {
    const skylights = parent?.type === "ROOF" ? docGetRoofSkylights(doc, parent as RoofHandle) : [];

    return {
      side: BuildingSide.FRONT,
      useNewPlacementMethod: true,
      name: props.name ?? docEntityNameWithCount("Skylight", skylights),
    } as Partial<SkylightPlacement>;
  },
  ROOF_OPENING: (props, doc, parent): Partial<DocEntityTypeToValueType["ROOF_OPENING"]> => {
    const roofOpenings = parent?.type === "ROOF" ? docGetRoofRoofOpenings(doc, parent as RoofHandle, props.side) : [];

    return {
      windowType: BuiltinWindowId.A3050,
      useNewPlacementMethod: true,
      name: props.name ?? docEntityNameWithCount("Roof Opening", roofOpenings),
    } as Partial<RoofOpeningPlacement>;
  },
  ELECTRICAL_DEVICE: (props): Partial<DocEntityTypeToValueType["ELECTRICAL_DEVICE"]> => {
    if (props.deviceType === ElectricalDeviceType.CONTROLLER) {
      return {
        labels: makeBuiltInLabelTable("addToElectricalPlan"),
      } as Partial<DocEntityTypeToValueType["ELECTRICAL_DEVICE"]>;
    }
    return {
      labels: {},
    } as Partial<DocEntityTypeToValueType["ELECTRICAL_DEVICE"]>;
  },
  COMPONENT: (props, doc): Partial<DocEntityTypeToValueType["COMPONENT"]> => {
    return {
      name: props?.name ?? docEntityNameWithCount("Component", Object.values(doc.components)),
    } as Partial<DocEntityTypeToValueType["COMPONENT"]>;
  },
  COMPONENT_INSTANCE: (props): Partial<DocEntityTypeToValueType["COMPONENT_INSTANCE"]> => {
    return {
      matrix: props?.matrix ?? mat3Translate(0, 0),
      mirrorX: props?.mirrorX ?? false,
      mirrorY: props?.mirrorY ?? false,
      mirrorCenterX: props?.mirrorCenterX ?? 0,
      mirrorCenterY: props?.mirrorCenterY ?? 0,
      labels: {},
    } as Partial<DocEntityTypeToValueType["COMPONENT_INSTANCE"]>;
  },
  PLAN_SET_TEMPLATE: (props): Partial<DocEntityTypeToValueType["PLAN_SET_TEMPLATE"]> => {
    return {
      ...props,
      exportPermissions: {
        ...defaultExportPermissions,
        ...props.exportPermissions,
      },
    };
  },
  PLAN_SET_TEMPLATE_CONFIG: (props, doc, parent): Partial<DocEntityTypeToValueType["PLAN_SET_TEMPLATE_CONFIG"]> => {
    assertIsNotUndefined(parent);
    const planSetTemplate = docGetEntity(doc, parent);
    assertIsDocEntityBase(planSetTemplate);
    const planSetTemplateConfigs = docEntityGetChildrenFromTable(doc.planSetTemplateConfigs, parent.id);
    const sortIndex = nextSortIndexFromTable(planSetTemplateConfigs);
    const name = props.name ?? docEntityNameWithCountFromTable("Plan Set Instance", planSetTemplateConfigs);
    return planSetTemplateConfigNew({ name, sortIndex });
  },
  CONTENT_TEMPLATE: (props, doc): Partial<DocEntityTypeToValueType["CONTENT_TEMPLATE"]> => {
    // Content templates are either drawing or sheet templates
    const templateType = props?.templateType ?? "DRAWING";
    const baseName = contentTemplateDefaultBaseName(props);

    const existingTemplates = objectFilter(doc.contentTemplates, (template) => template.templateType === templateType);

    const name = docEntityNameWithCount(props?.name ?? baseName, Object.values(existingTemplates));

    if (templateType === "DRAWING") {
      return {
        labels: {},
        ...props,
        templateType,
        name,
        viewScale: 1,
      };
    }

    return {
      labels: {},
      ...props,
      templateType,
      name,
    };
  },
  VIEWPORT: (props): Partial<DocEntityTypeToValueType["VIEWPORT"]> => {
    if (props.viewType !== "DRAWING") {
      const viewportProps = props as Partial<Exclude<Viewport, DrawingViewport>>;

      return {
        ...viewportProps,
        presetId: viewportProps.presetId ?? docEntityIdFromString("PRESET", BASE_PRESET_NAME),
      };
    }

    return {
      ...props,
    };
  },
  LABEL: (props, doc): Partial<DocEntityTypeToValueType["LABEL"]> => {
    return {
      ...props,
      name: props?.name ?? docEntityNameWithCount("Label", Object.values(doc.labels)),
    };
  },
  FOUNDATION_ASSEMBLY: (props, doc): Partial<DocEntityTypeToValueType["FOUNDATION_ASSEMBLY"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Foundation Assembly",
        Object.values(doc.foundationAssemblies).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  FOUNDATION_COMPONENT: (props, doc): Partial<DocEntityTypeToValueType["FOUNDATION_COMPONENT"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Foundation Component",
        Object.values(doc.foundationComponents).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  FRAME_FLOOR_ASSEMBLY: (props, doc): Partial<DocEntityTypeToValueType["FRAME_FLOOR_ASSEMBLY"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Frame Floor Assembly",
        Object.values(doc.frameFloorAssemblies).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  SLAB_FLOOR_ASSEMBLY: (props, doc): Partial<DocEntityTypeToValueType["SLAB_FLOOR_ASSEMBLY"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Slab Floor Assembly",
        Object.values(doc.slabFloorAssemblies).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  FRAMING_COMPONENT: (props, doc): Partial<DocEntityTypeToValueType["FRAMING_COMPONENT"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Framing Component",
        Object.values(doc.framingComponents).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  MASONRY_COMPONENT: (props, doc): Partial<DocEntityTypeToValueType["MASONRY_COMPONENT"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Masonry Component",
        Object.values(doc.masonryComponents).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  CIP_COMPONENT: (props, doc): Partial<DocEntityTypeToValueType["CIP_COMPONENT"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "CIP Component",
        Object.values(doc.cipComponents).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  SHEATHING_COMPONENT: (props, doc): Partial<DocEntityTypeToValueType["SHEATHING_COMPONENT"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Sheathing Component",
        Object.values(doc.sheathingComponents).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  HEADER_ASSEMBLY: (props, doc): Partial<DocEntityTypeToValueType["HEADER_ASSEMBLY"]> => {
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Header Assembly",
        Object.values(doc.headerAssemblies).map(({ name }) => ({ name })),
      );
    return {
      ...props,
      name,
    };
  },
  VIEW_TEMPLATE: (props): Partial<DocEntityTypeToValueType["VIEW_TEMPLATE"]> => {
    return {
      styleOverrides: {},
      ...props,
    };
  },
  VIEW_TEMPLATE_OVERRIDE: (props): Partial<DocEntityTypeToValueType["VIEW_TEMPLATE_OVERRIDE"]> => {
    return {
      ...props,
    };
  },
  VIEW_3D: (props, doc): Partial<DocEntityTypeToValueType["VIEW_3D"]> => {
    const id = props?.id ?? uid();
    const levelId = props?.levelId ?? null;
    const name =
      props?.name ??
      docEntityNameWithCount(
        "Custom 3D View",
        Object.values(doc.view3Ds).map(({ name }) => ({ name })),
      );
    const sortIndex = nextSortIndexFromTable(doc.view3Ds);
    return view3DNew(id, name, levelId, sortIndex);
  },
  ELEVATION_VIEW: (props, doc) => {
    const id = props?.id ?? uid();

    if (!props.referenceFrame) {
      console.warn("Elevation View must have a referenceFrame");
      return null;
    }

    return elevationViewNew(
      id,
      `${snakeCaseToTitleCase(props.referenceFrame)} Elevation`,
      props.referenceFrame,
      nextSortIndexFromTable(doc.elevationViews),
    );
  },
  PLAN_VIEW: (props, doc) => {
    const id = props?.id ?? uid();
    const levelIds = props?.levelIds ?? [];

    if (!props.referenceType) {
      console.warn("Plan View must have a referenceType");
      return null;
    }

    const name =
      props?.name ??
      docEntityNameWithCount(
        `Custom ${snakeCaseToTitleCase(props.referenceType)} View`,
        Object.values(doc.planViews).map(({ name }) => ({ name })),
      );

    return planViewNew(id, name, levelIds, props.referenceType, nextSortIndexFromTable(doc.planViews));
  },
  SECTION_VIEW: (props, doc) => {
    const id = props?.id ?? uid();
    const name =
      props?.name ??
      docEntityNameWithCount(
        props?.name ?? "Section View",
        Object.values(doc.sectionViews).map(({ name }) => ({ name })),
      );
    const sortIndex = nextSortIndexFromTable(doc.sectionViews);
    const { startX = 0, startY = 0, startZ = 0, endX = 0, endY = 0, endZ = 0 } = props ?? {};
    return sectionViewNew({ id, name, sortIndex, startX, startY, startZ, endX, endY, endZ });
  },
  VIEW_OVERRIDE: () => ({ action: "HIDE" }),
  UNDERLAY: () => ({ name: "Underlay", x: 0, y: 0, underlayViewType: "PLAN_VIEW" }),
  REFERENCE_GEOM: ({ geom }) => ({ type: "POLYLINE2", edgeIds: geom ? edgeIdsByPolyLine2(geom) : undefined }),
  COLUMN: (): Partial<DocEntityTypeToValueType["COLUMN"]> => ({
    version: "V2",
    catalogColumnId: defaultColumnStructuralColumnId,
    paletteId: defaultWoodPostMaterial,
    isolatedAssemblyId: undefined,
    verticalDatum: defaultRelativeVerticalDatumData,
    horizontalDatum: ColumnHorizontalDatum.ROOM_EDGE,
    lateralDatum: WallLateralDatumAnchorAll.ROOM_EDGE,
    horizontalAlignment: ColumnHorizontalAlignment.CENTER,
    lateralAlignment: ColumnLateralAlignment.CENTER,
    param: 0,
    paramNormalized: false,
    paramAnchor: "START",
    lateralOffset: 0,
    lateralOffsetNormalized: true,
    placement: defaultColumnPlacementV2(ColumnPlacementType.PARAM),
  }),
  ANNOTATION: (props): Partial<DocEntityTypeToValueType["ANNOTATION"]> => ({
    name: props?.type ? annotationTypeLabels[props.type] : "Annotation",
  }),
  PRESET: (props, doc): Partial<DocEntityTypeToValueType["PRESET"]> => {
    const name = props?.name ?? `Preset ${Object.values(doc.presets).reduce(newPresetInt, 1)}`;
    const sortIndex = nextSortIndexFromTable(doc.presets);

    const preset: Preset = {
      id: uid(),
      sortIndex,
      optionChoices: doc.optionChoices,
      name,
      type: props?.type ?? PresetType.PRESETS,
      entityType: "PRESET",
    };

    return preset;
  },
  CONTENT_TEMPLATE_INSTANCE: (
    props,
    doc,
    parent,
  ): Partial<DocEntityTypeToValueType["CONTENT_TEMPLATE_INSTANCE"]> | null => {
    if (!parent) {
      console.warn("Content Template Instance must have a parent");
      return null;
    }

    const template = doc.contentTemplates[props.contentTemplateId ?? ""];
    const templateType = template?.templateType ?? "DRAWING";

    let baseName: string = template?.name ?? (templateType === "DRAWING" ? "Drawing" : "Sheet");
    if (templateType === "SHEET") {
      baseName += " Instance";
    }

    const sheetIndexProps = templateType === "SHEET" ? { sheetNumberPrefix: "", sheetNumber: "" } : {};
    const siblings = docEntityGetChildrenFromTable(doc.contentTemplateInstances, parent.id);
    const sortIndex = nextSortIndexFromTable(siblings);

    return {
      ...sheetIndexProps,
      templateType,
      parameterValues: {},
      name: docEntityNameWithCount(baseName, Object.values(siblings)),
      sortIndex,
      labels: {},
    };
  },
  DATA_TABLE: (props): Partial<DocEntityTypeToValueType["DATA_TABLE"]> | null => {
    if (!props.dataSequenceId) {
      console.warn("DataTable must have a dataSequenceId");
      return null;
    }

    return {
      ...props,
      findAndReplace: {},
      columnConfig: {},
      presetId: props.presetId ?? docEntityIdFromString("PRESET", BASE_PRESET_NAME),
    };
  },
  LOCAL_PARAMETER: (_, doc, parent): Partial<DocEntityTypeToValueType["LOCAL_PARAMETER"]> | null => {
    if (!parent) {
      console.warn("Parameter must have a parent");
      return null;
    }

    const name = docEntityNameWithCount(
      "Parameter",
      Object.values(docEntityGetChildrenFromTable(doc.localParameters, parent?.id)),
    );

    return {
      name: parameterFormatName(name),
    };
  },
  GLOBAL_PARAMETER: (_, doc): Partial<DocEntityTypeToValueType["GLOBAL_PARAMETER"]> => {
    const name = docEntityNameWithCount("GlobalParameter", Object.values(doc.globalParameters));
    return {
      name: parameterFormatName(name),
    };
  },
  OPTION_RULE: (): Partial<DocEntityTypeToValueType["OPTION_RULE"]> => {
    return {
      overridable: true,
    };
  },
  SITE: () => defaultSiteValues,
  BLOCK: (props): Partial<DocEntityTypeToValueType["BLOCK"]> => {
    return {
      isHalfStory: false,
      suppressShutters: false,
      ...props,
    };
  },
  FACADE: (props): Partial<DocEntityTypeToValueType["FACADE"]> => {
    return {
      cornerBoardBehavior: null,
      insideCornerBoardBehavior: null,
      cornerBoardOffsetZ: 0,
      wainscotingBehavior: null,
      ...props,
    };
  },
  LEVEL: (props): Partial<DocEntityTypeToValueType["LEVEL"]> => {
    return {
      topOfWall: 108,
      enableAutoGeneratedFoundation: true,
      enableAutoGeneratedFloorSystem: true,
      placement: { type: LevelPlacementType.STACKED, automaticStacking: true },
      ...props,
    };
  },
  OPTION_SET: (props): Partial<DocOptionSet> => {
    return {
      options: {},
      labels: {},
      ...props,
    };
  },
  // `disabled` is required on the entity, so a row added without it reads as neither enabled nor
  // disabled downstream.
  OPTION_TEMPLATE: (props): Partial<DocEntityTypeToValueType["OPTION_TEMPLATE"]> => {
    return {
      disabled: false,
      ...props,
    };
  },
  SITE_SHAPE: (props, doc, parent): Partial<DocEntityTypeToValueType["SITE_SHAPE"]> | null => {
    if (!parent) {
      return null;
    }
    return {
      type: SiteShapeType.CONTOUR,
      name: props.name ?? "Contour",
      sortIndex: props.sortIndex ?? nextSortIndexFromTable(doc.siteShapes ?? {}),
      geom: polyLine2New([0, 0]),
      zHeight: 0,
      parentId: parent.id,
    };
  },
  ROOM: (props, doc, block): Partial<DocEntityTypeToValueType["ROOM"]> | null => {
    if (!block) {
      return null;
    }
    const blockRooms = docGetBlockRooms(doc, block);

    return {
      type: RoomType.GENERAL,
      edgeIds: props.outline ? edgeIdsByPolyLine2(props.outline) : undefined,
      name: docEntityNameWithCount("Room", blockRooms),
      useBuiltinLayout: false,
      removeBuiltInOpenings: false,
      labels: {},
    };
  },
  ELECTRICAL_LAYOUT: (props, doc): Partial<DocEntityTypeToValueType["ELECTRICAL_LAYOUT"]> => {
    const electricalLayouts = Object.values(doc.electricalLayouts);
    const layoutType: ElectricalLayoutType | undefined = props?.layout?.layoutType;
    const layoutTypeName = layoutType ? ElectricalLayoutTypeName[layoutType] : undefined;

    return {
      ...props,
      name: docEntityNameWithCount(layoutTypeName ?? "Electrical Layout", electricalLayouts),
    };
  },
  SETTINGS_SET: (props, doc, _): Partial<DocEntityTypeToValueType["SETTINGS_SET"]> => {
    const name: string = props.name ?? docEntityNameWithCountFromTable("Settings Set", doc.settingsSets ?? {});
    const id = props.id ?? uid();
    const sortIndex = props.sortIndex ?? nextSortIndexFromTable(doc.settingsSets ?? {});

    let settingsSet: SettingsSet = {
      type: SettingsSetBehaviorType.GLOBAL,
      ...props,
      entityType: "SETTINGS_SET",
      name,
      id,
      sortIndex,
    };

    const { globalSettingsSets, targetedSettingsSets } = docGetSettingsSetByBehaviorType(doc);
    const settingsSets = entityIsGlobalSettingsSet(settingsSet) ? globalSettingsSets : targetedSettingsSets;
    const baseName = `${snakeCaseToTitleCase(settingsSet.type)} Settings Set`;

    settingsSet = {
      ...settingsSet,
      name: docEntityNameWithCountFromTable(baseName, settingsSets),
    };

    return settingsSet;
  },
  SETTINGS: (props, _, parent): Partial<DocEntityTypeToValueType["SETTINGS"]> | null => {
    const defaultSettingsProps: { [key in SettingsType]?: Partial<SettingsFragment> } = {
      [SettingsType.ELECTRICAL_SETTINGS]: { mergeRules: true } as Partial<ElectricalSettings>,
    };

    if (!props.type || !parent) {
      console.error("Attempt to create a settings entity with missing type or parent");
      return null;
    }

    const defaultProps = defaultSettingsProps[props.type];

    const name = isBuiltInSettings(props.type)
      ? `${BUILT_IN_SETTINGS_TYPE_PREFIX} ${snakeCaseToTitleCase(props.type)}`
      : snakeCaseToTitleCase(props.type);

    return {
      ...defaultProps,
      ...props,
      name,
      parentId: parent.id,
    } as SettingsFragment;
  },
  ENTITY_SETTINGS: (props, doc): Partial<DocEntityTypeToValueType["ENTITY_SETTINGS"]> | null => {
    const { entityId, referencedEntityType } = props;

    if (!entityId) {
      return null;
    }

    const entityTypes: DocEntityType[] | undefined = referencedEntityType ? [referencedEntityType] : undefined;

    const handle = docGetEntityHandleById(doc, entityId, entityTypes);

    if (!handle || !isEntitySettingsEligibleHandle(doc, handle)) {
      return null;
    }

    const entitySettings = docGetEntitySettingsForEntity(doc, handle);
    const sortIndex = props.sortIndex ?? maxSortIndex(entitySettings) + 1;

    return {
      ...props,
      sortIndex,
    };
  },
  EFFECT: (props): Partial<DocEntityTypeToValueType["EFFECT"]> | null => {
    if (props.type === "STYLING") {
      return stylingEffectNew();
    } else if (props.type === "SCALING") {
      return scalingEffectNew();
    } else if (props.type === "VISIBILITY") {
      return visibilityEffectNew();
    } else if (props.type === "RELAXATION_OBSTACLE") {
      return relaxationObstacleEffectNew();
    } else {
      return null;
    }
  },
  RULE_BASED_SELECTOR: (props) => {
    return ruleBasedSelectorNew(props);
  },
  PROFILE: (props, doc): Partial<DocEntityTypeToValueType["PROFILE"]> => {
    const existingProfiles = Object.values(doc.profiles ?? {});

    switch (props.profileType) {
      case ProfileExtrusionDescriptorType.CUSTOM: {
        const name = props.name ?? docEntityNameWithCount("A New Custom Profile", existingProfiles);
        return customProfileEntityNew({ ...props, name });
      }
      case ProfileExtrusionDescriptorType.BOARD: {
        const name = props.name ?? docEntityNameWithCount("A New Rectangular Profile", existingProfiles);
        return boardProfileEntityNew({ ...props, name });
      }
      default: {
        const { profileType, ...rest } = props;
        void profileType;
        const name = props.name ?? docEntityNameWithCount("A New Rectangular Profile", existingProfiles);
        return boardProfileEntityNew({ ...rest, name });
      }
    }
  },
  RAILING_SETTING: (props, doc) => {
    const name = props.name ?? docEntityNameWithCount("Railing Setting", Object.values(doc.railingCatalog ?? {}));
    const { id, ...settingDefaults } = defaultRailingSetting;
    void id;
    return {
      ...settingDefaults,
      ...props,
      name,
    };
  },
  WALL_PLANE_FILTER: (props, doc) => {
    return wallPlaneFilterNew({
      name: docEntityNameWithCount("Wall Face Filter", Object.values(doc.wallPlaneFilters)),
      ...props,
    });
  },
  WALL_PLANE_RULE: (props, doc) => {
    const rules = Object.values(doc.wallPlaneRules ?? {});
    if (props.ruleKind === "WALL_PLANE") {
      return wallPlaneAnchoredRuleNew({
        name: docEntityNameWithCount("Wall Plane Rule", rules),
        planeHash: "",
        direction: "POSITIVE_NORMAL",
        ...props,
      });
    }
    return wallFaceRuleNew({
      name: docEntityNameWithCount("Rule", rules),
      ...props,
    });
  },
  WALL_PLANE_TRIM: (props, doc) => {
    const rules = Object.values(doc.wallPlaneRules ?? {}) as WallPlaneRuleEntity[];
    const firstRule = rules[0];
    assertIsNotUndefined(firstRule);
    return wallPlaneTrimEntityNew({
      name: docEntityNameWithCount("Wall Plane Trim", Object.values(doc.wallPlaneTrims ?? {})),
      ruleId: firstRule.id,
      path: polyLine2New([0, 0, 0, 0]),
      profileId: DEFAULT_TRIM_PROFILE_DESCRIPTOR.name,
      ...props,
    });
  },
  PROP: (props, doc) => {
    const { assets, propAttributes } = doc;
    const mergedPropLibrary = mergedPropLibraryNew(assets);
    return propCatalogItemNew({
      ...props,
      id: uid(),
      name: docEntityNameWithCount("New Prop", Object.values(propAttributes ?? {})),
      // Use an imported asset as the default if available.
      propAssetId: mergedPropLibrary.firstImportedAssetId() ?? DEFAULT_PROP_ID,
      category: PropCategory.FURNITURE,
    });
  },
  DATUM: (props, doc): Partial<DocEntityTypeToValueType["DATUM"]> | null => {
    const datums = Object.values(doc.datums);
    const sortIndex = props.sortIndex ?? nextSortIndexFromTable(doc.datums);

    switch (props.datumType) {
      case "WALL":
        switch (props.direction) {
          case "VERTICAL":
            return wallVerticalDatumEntityNew({
              ...props,
              name: props.name ?? docEntityNameWithCount("Facade Vertical Datum", datums),
              sortIndex,
            });
          case "HORIZONTAL":
            return wallHorizontalDatumEntityNew({
              ...props,
              name: props.name ?? docEntityNameWithCount("Facade Horizontal Datum", datums),
              sortIndex,
            });
          default:
            return null;
        }
      case "FOUNDATION":
        switch (props.direction) {
          case "VERTICAL":
            return foundationVerticalDatumEntityNew({
              ...props,
              name: props.name ?? docEntityNameWithCount("Foundation Vertical Datum", datums),
              sortIndex,
            });
          case "LATERAL":
            return foundationLateralDatumEntityNew({
              ...props,
              name: props.name ?? docEntityNameWithCount("Foundation Lateral Datum", datums),
              sortIndex,
            });
          default:
            return null;
        }
      case "LEVEL":
        return levelDatumEntityNew({
          ...props,
          name: props.name ?? docEntityNameWithCount("Level Datum", datums),
          sortIndex,
        });
      case "SHAREABLE":
        return absoluteVerticalDatumEntityNew({
          ...props,
          name: props.name ?? docEntityNameWithCount("Origin Relative Datum", datums),
          sortIndex,
        });
      case "WALL_PLANE":
        switch (props.direction) {
          case "HORIZONTAL":
            return wallPlaneHorizontalDatumEntityNew({
              ...props,
              name: props.name ?? docEntityNameWithCount("Wall Plane Horizontal Datum", datums),
              sortIndex,
            });
          default:
            return null;
        }
      default:
        return null;
    }
  },
};

// this validation is used to ensure that the edgeId is present in the entities in EdgeBasedEntityTypes
export type EdgeIdBasedEntityTypes = keyof {
  [P in Extract<
    TableBasedAddDocEntityType,
    "ANNOTATION" | "PROP_INSTANCE" | "OPENING" | "EDGE_BEHAVIOR" | "COLUMN"
  >]: Pick<DocEntityTypeToValueType[P], "edgeId">;
};

const isEdgeIdBasedEntityTypeLookup: {
  [key in EdgeIdBasedEntityTypes]: true;
} = {
  ANNOTATION: true,
  PROP_INSTANCE: true,
  OPENING: true,
  EDGE_BEHAVIOR: true,
  COLUMN: true,
};

function isEdgeIdBasedEntityType(type: DocEntityType, entity: DocEntity): boolean {
  if (isEdgeIdBasedEntityTypeLookup[type]) {
    return true;
  }

  if (isCaseWorkEntity(entity)) {
    switch (entity.type) {
      case "DYNAMIC":
        return false;

      default:
        return true;
    }
  }

  return false;
}

type DocEntityPropertyVerifier<T extends TableBasedAddDocEntityType> = (
  props: DocEntityTypeToValueType[T],
  doc: Doc,
) => void;

/**
 * Use this to verify that an entity has the required properties
 */
const docEntityTypeToPropertyVerifier: {
  [P in TableBasedAddDocEntityType]?: DocEntityPropertyVerifier<P>;
} = {
  ROOM: (props, doc) => {
    if (!props.parentId) {
      throw new Error("Room must have a parentId");
    }

    if (!props.outline) {
      throw new Error("Room must have a outline");
    }

    if (!doc.blocks[props.parentId]) {
      throw new Error("Room parentId is invalid");
    }
  },
  REFERENCE_GEOM: (props) => {
    if (!props.geom) {
      throw new Error("ReferenceGeom must have geom");
    }
  },
  PRESET: (props) => {
    if (!props.optionChoices) {
      throw new Error("Preset must have optionChoices");
    }
  },
};

export default function docAddEntity<T extends TableBasedAddDocEntityType>(
  doc: Doc,
  type: T,
  props: Partial<DocEntityTypeToValueType[T]>,
  parent?: DocEntityTypeToParentHandleType[T],
  options?: AddEntityOptions,
): Doc {
  const result = docCanAddEntity(doc, type, props, parent);
  if (result.canAddEntity === false) {
    console.warn(result.log);
    return doc;
  }

  const tableName = docEntityTypeToDocTableName[type];

  if (!tableName) {
    console.warn(`Could not find table name for ${type}`);
    return doc;
  }

  const table = (doc[tableName] ?? {}) as Table<{ name: string; sortIndex: number }>;
  const tableEntities = Object.values(table);
  /**
   * Beware! This code casts to any to avoid a very expensive typechecking operation.
   */

  let entity = {
    name: docEntityNameWithCount(snakeCaseToTitleCase(type), tableEntities),
    ...docEntityTypeToDefaultValues[type]?.(props, doc, parent),
    entityType: type,
    id: uid(),
    sortIndex: docEntityTypeToDefaultSortIndex[type]?.(props, doc, parent) ?? nextSortIndexFromTable(table),
    ...props,
  } as any as Partial<DocEntityTypeToValueType[T]> & { id: string };

  if (parent) {
    let parentId = docGetEntityId(doc, parent);

    if (isRoomLayoutRelatedHandle(parent)) {
      const { roomHandle, edgeId: edgeIfNoneSpecified } = getRoomHandleAndEdgeIdFromHandle(
        parent,
        doc.rooms,
        doc.roomLayouts,
        doc.roomLayoutInstances,
      );
      parentId = roomHandle?.id ?? parentId;
      doc = docAddEntityToRoomLayoutIfRoomLayoutHandle(parent, doc, type as RoomLayoutEntityType, entity.id);

      if (isEdgeIdBasedEntityType(type, entity)) {
        const edgeId = "edgeId" in entity ? entity.edgeId : edgeIfNoneSpecified;
        entity = {
          ...entity,
          edgeId,
        };
      }
    } else if (isRoofRelativeHandle(parent)) {
      const { roofHandle, edgeId: edgeIfNoneSpecified } = getRoofHandleAndEdgeIdFromHandle(parent);
      parentId = roofHandle?.id ?? parentId;

      if (isEdgeIdBasedEntityType(type, entity)) {
        const edgeId = "edgeId" in entity ? entity.edgeId : edgeIfNoneSpecified;
        entity = {
          ...entity,
          edgeId,
        };
      }
    }

    entity = {
      ...entity,
      parentId,
    };
  }

  docEntityTypeToPropertyVerifier[type]?.(entity as DocEntityTypeToValueType[T], doc);
  // Tables may be missing on older docs at runtime (e.g. wallPlaneTrims / freeformWalls) — materialize before merge.
  if (doc[tableName] === undefined) {
    doc = { ...doc, [tableName]: {} };
  }
  doc = mergeIn(doc, [tableName, entity.id], entity);

  if (!entity?.id) {
    return doc;
  }

  const tableBasedDocEntityHandle = { type, id: entity.id } as DocEntityHandle;
  doc = docApplySideEffectsForNewEntity(doc, tableBasedDocEntityHandle, options);

  return doc;
}

type CanAddEntityResult = { readonly canAddEntity: true } | { readonly canAddEntity: false; readonly log: LogEntry };

function logErr(message: string): CanAddEntityResult {
  return { canAddEntity: false, log: newLog(message, "ERROR") };
}

export function docCanAddEntity<T extends TableBasedAddDocEntityType>(
  doc: Doc,
  type: T,
  props: Partial<DocEntityTypeToValueType[T]>,
  parent?: DocEntityTypeToParentHandleType[T],
): CanAddEntityResult {
  // if an entity requires a parent, then verify that was provided
  if (!parent && docEntityTypeRequiresParent(type)) {
    console.error(`Attempting to add a ${type} entity without a parent`);
    return logErr("Entity requires a parent");
  }

  // make sure the entity parent is valid
  if (parent && !docGetEntity(doc, parent)) {
    console.error(`Attempting to add a ${type} entity with an invalid or missing parent`, { invalidParent: parent });
    return logErr(`Non-existent parent entity`);
  }

  const parentEntityRef = parent ? docEntityHandleToEntityRef(doc, parent) : null;
  const entityRefMutableChildTypes: DocEntityType[] | undefined = parentEntityRef
    ? docRefEligibleEntityTypeToMutableChildTypes[parentEntityRef?.entityType]
    : undefined;

  if (parentEntityRef && !entityRefMutableChildTypes?.includes(type)) {
    console.error(`Attempting to add an ${type} entity to a doc ref'd parent`);
    return logErr(`Cannot add an ${type} entity to a doc ref'd parent`);
  }

  if (parent && docEntityOrParentIsLocked(doc, parent)) {
    return logErr(`Cannot add ${snakeCaseToTitleCase(type)} to a locked ${snakeCaseToTitleCase(parent.type)}`);
  }

  switch (type) {
    case "CONTENT_TEMPLATE_INSTANCE": {
      const templateId = (props as any as Partial<ContentTemplateInstance>).contentTemplateId;

      if (!templateId) {
        return logErr("Content template instance must have a template id");
      }

      const templateHandle: ContentTemplateHandle = { type: "CONTENT_TEMPLATE", id: templateId };

      return docCanAddContentTemplateInstance(
        doc,
        templateHandle,
        parent as DocEntityTypeToParentHandleType["CONTENT_TEMPLATE_INSTANCE"],
      );
    }
    case "LABEL": {
      if (!labelNamePredicate(doc.labels, props as any as Partial<Label>)) {
        return logErr(`A label with the name "${props.name}" already exists`);
      }
      break;
    }
    case "REPORTABLE_UNIT_DESIGNATION": {
      const labelId = (props as any as Partial<ReportableUnitDesignation>).labelId;
      if (!labelId) {
        return logErr("Reportable unit designation must have a label id");
      }

      // Check that no entity with this label is a component member or an annotation hosted in a component
      const entitiesWithLabel = docGetLabelableEntitiesWithLabel(doc, labelId);
      for (const entityHandle of entitiesWithLabel) {
        if (docEntityGetComponentOrAnnotationHost(doc, entityHandle)) {
          return logErr(
            "Cannot designate this label as a reportable unit: an entity with this label is a member of a component",
          );
        }
      }

      if (docReportableUnitDesignationBreaksOptionSetCap(doc, labelId)) {
        return logErr(
          "Cannot designate this label as a reportable unit: an option set with this label already has a reportable unit label",
        );
      }
      break;
    }
    case "VIEWPORT":
    case "DATA_TABLE":
      const parentContentTemplate = docGetTableEntity(
        doc,
        parent as DocEntityTypeToParentHandleType["VIEWPORT" | "DATA_TABLE"],
      );
      if (
        parentContentTemplate &&
        parentContentTemplate.entityType === "CONTENT_TEMPLATE" &&
        !contentTemplateAllowsViewportOrDataTable(parentContentTemplate)
      ) {
        return logErr(
          `Cannot add a ${snakeCaseToTitleCase(type)} to a ${getContentTemplateSubType(
            parentContentTemplate,
          )} Template`,
        );
      }
      break;
    case "ENTITY_SETTINGS": {
      const { entityId, referencedEntityType } = props as any as Partial<EntitySettings>;
      if (!entityId) {
        return logErr("Entity settings must have an entity id");
      }

      if (!referencedEntityType) {
        return logErr("Entity settings must have a referenced entity type");
      }
      break;
    }
    default: {
      return { canAddEntity: true };
    }
  }

  return { canAddEntity: true };
}

export interface AddEntityOptions {
  readonly omitSelection?: boolean;
  readonly refHandles?: DocEntityHandle[];
  readonly groupHandle?: GroupHandle; // group to add entity to
  readonly skipDefaults?: boolean;
  readonly componentLevelOldToNewIdOut?: Record<string, string>;
}

/**
 * Certain entities, when added, should apply secondary side effects to the Doc.
 */
export function docApplySideEffectsForNewEntity(doc: Doc, handle: DocEntityHandle, options?: AddEntityOptions): Doc {
  if (isTableBasedDocEntityType(handle.type) && !options?.skipDefaults) {
    switch (handle.type) {
      case "LABEL":
        doc = docMergeLabelTableProps(doc, options?.refHandles as LabelableEntityHandle[], { [handle.id]: true });
        break;
      case "RULE_BASED_SELECTOR":
        if (options?.refHandles?.length === 1 && options.refHandles[0].type === "VIEW_TEMPLATE_OVERRIDE") {
          doc = docAddEntity(doc, "RULE_BASED_SELECTOR_INSTANCE", { selectorId: handle.id }, options.refHandles[0]);
        }
        break;
      case "COMPONENT": {
        // Auto-associate selected entities to the new component when provided via refHandles.
        const refHandles = options?.refHandles ?? [];
        const memberHandles = refHandles.filter(isComponentMemberEntityHandle);
        doc = docCopyStructureForMembers(
          doc,
          handle.id,
          memberHandles,
          options?.componentLevelOldToNewIdOut,
          refHandles,
        );
        doc = docAddComponentMembers(doc, handle.id, memberHandles);
        doc = docEnforceComponentStructure(doc, handle.id).doc;
        break;
      }
      case "COMPONENT_INSTANCE": {
        // Get the component instance entity to access its componentId.
        const componentInstance = docGetTableEntity(doc, handle);
        if (!componentInstance || componentInstance.entityType !== "COMPONENT_INSTANCE") {
          break;
        }

        const componentHandle: ComponentHandle = {
          type: "COMPONENT",
          id: componentInstance.componentId,
        };

        // Find all option sets that are members of the component (recursively).
        const optionSetHandles = docGetComponentRecursiveOptionSets(doc, componentHandle);

        // For each option set, create an instance option set and add it to the instanceOptionSets table.
        const instanceOptionSets: Record<string, InstanceOptionSet> = {};
        const optionChoices: Mutable<OptionChoices> = {};

        for (const optionSetHandle of optionSetHandles) {
          const instanceOptionSetId = deriveInstanceOptionSetId(optionSetHandle.id, handle.id);
          instanceOptionSets[instanceOptionSetId] = {
            id: instanceOptionSetId,
            name: `${componentInstance.name} Option Set`,
            entityType: "INSTANCE_OPTION_SET",
            archetypeId: optionSetHandle.id,
            componentInstanceId: handle.id,
            disabled: false,
            sortIndex: 0,
          };

          // Initialize instance choice with a copy of the archetype's current value so
          // the instance starts independent — changing the archetype won't affect it.
          const archetypeOptionSet = doc.optionSets[optionSetHandle.id];
          const archetypeChoice = doc.optionChoices?.[optionSetHandle.id] ?? archetypeOptionSet?.defaultValue ?? null;
          if (archetypeChoice !== null) {
            optionChoices[instanceOptionSetId] = archetypeChoice;
          }
        }

        const pathValuePairs: [(string | number)[], Record<string, unknown>][] = [];
        if (Object.keys(instanceOptionSets).length > 0) {
          pathValuePairs.push([["instanceOptionSets"], instanceOptionSets]);
        }
        if (Object.keys(optionChoices).length > 0) {
          pathValuePairs.push([["optionChoices"], optionChoices]);
        }
        doc = batchMergeIn(doc, pathValuePairs);
        break;
      }
      case "ELEVATION":
        doc = docMergeElevationOptionSet(doc);
        break;
      case "ROOM":
        doc = docAddDefaultRoomFloorFinish(doc, { type: handle.type, id: handle.id });
        doc = docAddDefaultRoomCeiling(doc, { type: handle.type, id: handle.id });
        break;
      case "VIEW_TEMPLATE_OVERRIDE":
        doc = docMergeViewTemplateOverridesProps(
          doc,
          options?.refHandles as ViewTemplateHandle[],
          docEntityIdFromString("VIEW_TEMPLATE_OVERRIDE", handle.id),
        );
      default:
        break;
    }
  }

  if (options?.groupHandle && docEntityIsGroupable(doc, handle)) {
    const hash = docEntityHandleHash(handle);
    const existingGroupId = doc.entityGroups[hash];

    // If the entity is already in the group, do nothing
    if (existingGroupId === options.groupHandle.id) {
      return doc;
    }

    // If the group is doc ref'd but the entity is not, don't add it to the group
    const groupEntityRef = docEntityHandleToEntityRef(doc, options.groupHandle);
    const entityRef = docEntityHandleToEntityRef(doc, handle);

    if (groupEntityRef && !entityRef) {
      return doc;
    }

    // Otherwise, add the entity to the group
    return docAddEntitiesToGroup(doc, handle, options.groupHandle);
  }

  return doc;
}

export function docAddDefaultRoomFloorFinish(doc: Doc, roomHandle: RoomHandle): Doc {
  const room = docGetEntity(doc, roomHandle) as StaticRoom;
  // add default finishes to new rooms
  if (room) {
    doc = docAddEntity(
      doc,
      "FLOOR_FINISH",
      {
        name: "Default Floor Finish",
        ...floorAssemblyDynamicFinishLayerOperatorNew("ADD"),
      },
      roomHandle,
    );
  }
  return doc;
}

export function docAddDefaultRoomCeiling(doc: Doc, roomHandle: RoomHandle): Doc {
  const room = docGetEntity(doc, roomHandle) as StaticRoom;
  // Add default ceiling to new rooms.
  if (room) {
    doc = docAddEntity(doc, "CEILING", { type: CeilingType.FLAT }, roomHandle);
  }
  return doc;
}

export function docCanAddContentTemplateInstance(
  doc: Doc,
  contentTemplate: ContentTemplateHandle,
  parent: DocEntityTypeToParentHandleType["CONTENT_TEMPLATE_INSTANCE"],
): CanAddEntityResult {
  const template = docGetTableEntity(doc, contentTemplate);
  if (!template) {
    return logErr("Non-existent template");
  }

  if (
    (parent?.type === "CONTENT_TEMPLATE" || parent?.type === "CONTENT_TEMPLATE_INSTANCE") &&
    !docContentTemplateInstanceCanBeInsertedWithoutSelfReference(
      doc,
      contentTemplate,
      parent as ContentTemplateHandle | ContentTemplateInstanceHandle,
    )
  ) {
    return logErr("Cannot add a template instance inside itself");
  }

  const parentContentTemplate =
    parent.type === "CONTENT_TEMPLATE" || parent.type === "CONTENT_TEMPLATE_INSTANCE"
      ? docGetTableEntity(doc, parent)
      : null;

  if (isDrawingTemplate(template) && !isValidParentForDrawingTemplateInstance(template, parentContentTemplate)) {
    return logErr(`Invalid parent for drawing instance`);
  }

  return { canAddEntity: true };
}
